import { Button, Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { DeliveryMode } from "api/resources/learningItems/types"
import clsx from "clsx"
import SimpleCard from "components/SimpleCard"
import CheckRestrictions from "features/AccessManagement/CheckRestrictions"
import CourseFeedbackCard from "features/CourseFeedbackCard"
import { ResumeLearningCard } from "features/Home/ResumeLearning"
import { useImpactCourseTour } from "features/Home/SuraasaPlusNewYearOnboarding/ImpactCourseTour/useImpactCourseTour"
import OverviewStatsCard from "features/LearningItems/Analytics/OverviewStatsCard"
import { useOverviewAnalytics } from "features/LearningItems/Analytics/useOverviewAnalytics"
import ForYou from "features/LearningItems/ForYou"
import NextUp from "features/LearningItems/Qualification/QualificationOverview/NextUp"
import { useLaunchPracticeInterview } from "features/LearningModule/hooks/useLaunchPracticeInterview"
import {
  buildSyntheticItems,
  deriveSyntheticItemStatuses,
  isSyntheticItemId,
} from "features/LearningModule/utils"
import { useMemo } from "react"
import { Link, useParams } from "react-router-dom"
import routes from "routes"
import { learningItemSlugs } from "utils/constants"
import { getLearningItemURL, pluralize } from "utils/helpers"
import { useEnrollments } from "utils/hooks/useEnrollments"

import { useCourseDetails } from ".."
import CourseAssessments from "../Assessments"
import Classes from "../Classes"
import CompletedCourseCard from "../CompletedCourseCard"
import Curriculum from "../Curriculum"
import { getLearningItemDisplayName } from "./utils"

const recordedClassesDeliveryModes = [
  DeliveryMode.EXECUTIVE,
  DeliveryMode.PROFESSIONAL,
]
const liveClassesDeliveryModes = [DeliveryMode.EXECUTIVE, DeliveryMode.TDC]
const classesEligibleDeliveryModes = [
  ...new Set([...recordedClassesDeliveryModes, ...liveClassesDeliveryModes]),
]

type Props = {
  className?: string
}

const CourseOverview = ({ className }: Props) => {
  const { slug, parentSlug } = useParams() as {
    slug: string
    parentSlug?: string
  }
  const learningItemType = "course"

  const { course, accessStatus } = useCourseDetails()

  const { data } = useQuery({
    queryKey: queries.credentials.getUserCredentials(
      learningItemType,
      course.id
    ).queryKey,
    queryFn: () =>
      api.credentials.getUserCredentials({
        urlParams: { itemType: learningItemType, itemId: course.id },
      }),
  })

  const { data: overview } = useQuery({
    queryFn: () =>
      api.learningItems.overview({
        urlParams: {
          itemType: "course",
          itemId: course.id,
        },
      }),
    enabled: Boolean(course.id),
    queryKey: queries.learningItems.overview("course", course.id).queryKey,
  })

  const courseIsCompleted =
    overview?.progress &&
    overview.progress.completedUserPlannerItems ===
      overview.progress.totalUserPlannerItems

  const curriculum = useQuery({
    enabled: !!course.id,
    queryFn: () =>
      api.learningItems.listPlannerItems({
        urlParams: { plannerId: course?.id },
      }),
    queryKey: queries.learningItems.listPlannerItems(course?.id).queryKey,
  })

  const isBEv2Module = slug === learningItemSlugs.boostingEmployabilityV2

  const {
    pgctl: { deliveryMode, isNewDeliveryMode },
    cpat,
  } = useEnrollments()

  const launchQuery = useLaunchPracticeInterview(
    Boolean(isBEv2Module && curriculum.data && curriculum.data.length > 0)
  )

  const curriculumItems = useMemo(() => {
    if (!curriculum.data) return curriculum.data
    if (!isBEv2Module || curriculum.data.length === 0) return curriculum.data

    const { aiInterviewStatus, cpatStatus } = deriveSyntheticItemStatuses({
      launchData: launchQuery.data,
      cpatProgress: cpat.latestCPAT?.progress ?? 0,
    })

    const syntheticItems = buildSyntheticItems(curriculum.data).map(item => {
      if (item.learningContentType === "ai_interview")
        return { ...item, status: aiInterviewStatus }
      if (item.learningContentType === "cpat")
        return { ...item, status: cpatStatus }
      return item
    })

    return [...curriculum.data, ...syntheticItems]
  }, [
    curriculum.data,
    isBEv2Module,
    launchQuery.data,
    cpat.latestCPAT?.progress,
  ])

  useImpactCourseTour({
    isLoading: curriculum.isLoading,
    page: "course",
  })

  const lastAccessedItem = curriculumItems
    ?.toReversed()
    .filter(
      x =>
        x.learningContentType !== "pre_recorded_class" &&
        !isSyntheticItemId(x.id)
    )
    .find(
      item =>
        item.status === "completed" ||
        item.status === "unlocked" ||
        item.status === "in_progress" ||
        item.status === "incomplete"
    )

  const userScore = useQuery({
    enabled: Boolean(course.id),
    queryKey: queries.profile.userScore(learningItemType, course.id).queryKey,
    queryFn: () =>
      api.profile.credentials.getUserScore({
        urlParams: {
          learningItemType: learningItemType,
          learningItemId: course.id,
        },
      }),
  })

  const {
    analyticsData,
    isAnalyticsError,
    timeSpentQuery,
    selectedTimeInterval,
    setSelectedTimeInterval,
    sortedTerms,
  } = useOverviewAnalytics({
    itemId: course.id,
    itemType: course.type,
  })

  return (
    <div className={clsx(className, "flex flex-col gap-4")}>
      {/* This is a container for banners */}

      <CompletedCourseCard
        serialNumber={data?.serialNumber}
        hasGradebook={userScore.data?.creditsEligibility || false}
        isCourseCompleted={courseIsCompleted || false}
        learningItemId={course.id}
      />

      {courseIsCompleted && (
        <CourseFeedbackCard
          itemId={course.id}
          courseName={course.name}
          onClose={() => {
            // sessionStorage.setItem("hasDismissedRateCoursePopup", "true")
          }}
        />
      )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:gap-x-6">
        <div className="flex flex-col gap-[42px] lg:col-span-8">
          <OverviewStatsCard
            isCourse
            analyticsData={analyticsData}
            isAnalyticsError={isAnalyticsError}
            timeSpentQuery={timeSpentQuery}
            selectedTimeInterval={selectedTimeInterval}
            setSelectedTimeInterval={setSelectedTimeInterval}
            sortedTerms={sortedTerms}
          />

          {overview && lastAccessedItem && !courseIsCompleted && (
            <div>
              <Typography variant="title3" className="mb-1">
                Continue
              </Typography>
              <CheckRestrictions
                accessStatus={accessStatus}
                render={_ => (
                  // Restriction is already catered inside the component
                  <ResumeLearningCard
                    href={getLearningItemURL({
                      route: parentSlug
                        ? routes.learningModuleWithParent
                        : routes.learningModule,
                      itemSlug: slug,
                      parentSlug: parentSlug,
                      parentType: learningItemType,
                      queryParams: { plannerId: course.id.toString() },
                    })}
                    item={{
                      slug,
                      name: getLearningItemDisplayName(lastAccessedItem),
                      learningItemType,
                      image: "image" in course ? course.image : null,
                    }}
                    // disabled={isRestricted}
                    progress={overview.progress}
                  />
                )}
              />
            </div>
          )}

          <Curriculum
            plannerId={course.id}
            data={curriculumItems}
            isError={curriculum.isError}
            isLoading={curriculum.isLoading}
            slug={slug}
          />
          {isNewDeliveryMode && <CourseAssessments hideIfNoData />}
          {deliveryMode &&
            classesEligibleDeliveryModes.includes(deliveryMode) && (
              <Classes
                recordedClasses={
                  curriculum.isSuccess &&
                  recordedClassesDeliveryModes.includes(deliveryMode)
                    ? curriculum.data.filter(
                        x => x.learningContentType === "pre_recorded_class"
                      )
                    : []
                }
                showLive={liveClassesDeliveryModes.includes(deliveryMode)}
                hideIfNoData
              />
            )}
          {overview && (
            <div>
              <Typography variant="title3" className="mb-1">
                Skill Evidences (Assignments)
              </Typography>
              <SimpleCard className="flex flex-wrap items-center justify-between gap-1 border-interactive-200 bg-interactive-50">
                <Typography variant="title4" className="text-interactive-700">
                  {overview.dueAssignments === 0
                    ? "No Assignments Due"
                    : `${pluralize("Assignment", overview.dueAssignments)} Due`}
                </Typography>

                <Link to="../assignments">
                  <Button size="sm" className="bg-interactive-500">
                    View Assignments
                  </Button>
                </Link>
              </SimpleCard>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 lg:col-span-4">
          <NextUp learningItemType={course.type} learningItemId={course.id} />

          <ForYou />
        </div>
      </div>
    </div>
  )
}

export default CourseOverview
